import type { GQL_ID } from '@@partner:types:graphql'
import type { OrderInfo, OrderPosition } from '@@partner:types:app'

import React from 'react'
import { formatPrice } from '@@partner:helpers/util'
import { getAmountStep, getCountPostfix } from '@@partner:helpers/product'

import {
  Tag,
  Checkbox,
  FlexboxGrid,
  Panel,
  Grid,
  Col
} from 'rsuite'
import CommonOrderPositionStatusLabel from '@components/common/CommonOrderPositionStatusLabel'
import ProductCardControls from '@components/order/product/ProductCardControls'

import './RefundPositionProductsTable.scss'
import { generatePath, Link } from 'react-router-dom'
import { ROUTE_CATALOG_PRODUCT_EDIT } from '@@partner:config/routes'
import { usePartner } from '@@partner:helpers/hooks/auth'

export interface RefundPositionProductTableProps {
  checkedIds: GQL_ID[]
  order: OrderInfo
  groupedPositions: Record<GQL_ID, RefundPositionInfo>
  updateRefundAmount(positionId: GQL_ID, refundAmount: number): void
  makeRefund: boolean
}

export interface RefundPositionInfo {
  id: GQL_ID
  checked: boolean
  isRefundable: boolean
  refundAmount: number
  positions: OrderPosition
}

export default function RefundPositionProductsTable ({
  order,
  groupedPositions,
  updateRefundAmount,
  checkedIds,
  makeRefund
}: RefundPositionProductTableProps) {
  const { brandSlug } = usePartner()
  const { customerProducts: positions = [] } = order ?? {}

  return (
    <div className="RefundPositionProductsTable">
      {positions.map((position) => (
        <Panel key={position.id} shaded className="RefundPositionProductsTable__Item">
          <FlexboxGrid justify="space-between" align="middle">
            {!!makeRefund && (
              <Col sm={1} xs={24} className="RefundPositionProductsTable__Checkbox">
                <Checkbox
                  disabled={!groupedPositions[position.id]?.isRefundable}
                  key={position.id}
                  value={position.id}
                />
              </Col>
            )}
            <Col xs={24} sm={4} className="RefundPositionProductsTable__PreviewContainer">
              <img
                className="RefundPositionProductsTable__Preview"
                src={position?.product?.preview?.url}
                alt={position?.product.name}
              />
            </Col>
            <Col xs={24} sm={makeRefund ? 10 : 12}>
              <div className="RefundPositionProductsTable__Info">
                <Link
                  to={generatePath(ROUTE_CATALOG_PRODUCT_EDIT, {
                    productId: position.product.id,
                    brandSlug
                  })}
                  target="_blank"
                >
                  <span className="RefundPositionProductsTable__Info__Name">
                    {position.name}
                  </span>
                  <div>
                    Артикул: {position.product.article_num ?? position.product.xid}
                  </div>
                </Link>
                <span className="RefundPositionProductsTable__Info__Status">
                  Статус:{' '}
                  <CommonOrderPositionStatusLabel status={position.status} />
                </span>
              </div>
            </Col>
            <Col xs={24} sm={makeRefund ? 9 : 8}>
              <div className="RefundPositionProductsTable__Info">
                <span className="RefundPositionProductsTable__Info__Amount">
                  Количество:{' '}
                  <Tag>
                    {position.assembled_count}
                    {position.count !== position.assembled_count && ` / ${position.count}`}
                    {' '}{getCountPostfix(position.product, false)}
                  </Tag>
                </span>
                <span className="RefundPositionProductsTable__Info__Price">
                  Цена: <Tag color="green">{formatPrice(position.price)}</Tag>
                </span>
                <span className="RefundPositionProductsTable__Info__Total">
                  Стоимость:{' '}
                  <Tag color="violet">
                    {formatPrice(position.assembled_total ?? 0)}
                  </Tag>
                </span>
              </div>
            </Col>
          </FlexboxGrid>
          {checkedIds.includes(position.id) && (
            <div className='RefundPositionProductsTable__Info__Control'>
              <ProductCardControls
                amount={groupedPositions[position.id]?.refundAmount ?? 0}
                amountStep={getAmountStep(position.product)}
                maxAmount={position.assembled_count}
                onAmountChange={(value) =>
                  updateRefundAmount(position.id, Number(value))
                }
              />
              <small>
                Макс: {position.assembled_count}{' '}
                {getCountPostfix(position.product, false)}
              </small>
            </div>
          )}
        </Panel>
      ))}
    </div>
  )
}
